import { Card, FoundationIndex, Value } from "./types";
import { FOUNDATION_SEQUENCES, VALUES } from "./constants";

/**
 * Creates a new shuffled deck of cards
 * Returns the shuffled deck and the foundation cards (A, 2, 3, 4)
 */
export const createDeck = (): { deck: Card[]; foundationCards: Card[] } => {
  const newDeck: Card[] = [];
  // Create full deck (4 of each value for a total of 52 cards)
  VALUES.forEach((value) => {
    // Create 4 cards of each value (representing what would have been 4 suits)
    for (let i = 0; i < 4; i++) {
      newDeck.push({ value });
    }
  });

  // Remove A, 2, 3, 4 for foundations (one of each)
  const foundationCards: Card[] = [];
  (["A", "2", "3", "4"] as Value[]).forEach((value) => {
    const idx = newDeck.findIndex((card) => card.value === value);
    if (idx !== -1) {
      foundationCards.push(newDeck.splice(idx, 1)[0]);
    }
  });

  // Shuffle the remaining cards
  for (let i = newDeck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
  }

  return { deck: newDeck, foundationCards };
};

/**
 * Checks if a card can be played on a foundation
 */
export const canPlayOnFoundation = (
  card: Card,
  foundationIndex: FoundationIndex,
  foundations: Card[][],
): boolean => {
  const foundation = foundations[foundationIndex];

  if (foundation.length === 0) {
    // Should not happen as foundations start with cards
    return false;
  }

  const lastCard = foundation[foundation.length - 1];
  const lastCardIndex = FOUNDATION_SEQUENCES[foundationIndex].indexOf(
    lastCard.value,
  );

  // Check if the card is the next in sequence for this foundation
  return (
    lastCardIndex !== -1 &&
    lastCardIndex < FOUNDATION_SEQUENCES[foundationIndex].length - 1 &&
    card.value === FOUNDATION_SEQUENCES[foundationIndex][lastCardIndex + 1]
  );
};

/**
 * Gets all valid foundation destinations for a card
 */
export const getValidFoundationDestinations = (
  card: Card | null,
  foundations: Card[][],
): FoundationIndex[] => {
  if (!card) return [];

  const validDestinations: FoundationIndex[] = [];
  for (let i = 0; i < 4; i++) {
    if (canPlayOnFoundation(card, i as FoundationIndex, foundations)) {
      validDestinations.push(i as FoundationIndex);
    }
  }
  return validDestinations;
};

/**
 * Checks if the game is won
 */
export const isGameWon = (foundations: Card[][]): boolean => {
  const totalCards = foundations.reduce((sum, pile) => sum + pile.length, 0);
  return totalCards === 52;
};

/**
 * Checks if the game is over (no more moves possible)
 */
export const isGameOver = (
  stock: Card[],
  currentCard: Card | null,
  tableau: Card[][],
  foundations: Card[][],
): boolean => {
  if (stock.length > 0 || currentCard || isGameWon(foundations)) {
    return false;
  }

  // Check if any tableau cards can be moved to foundations
  for (let t = 0; t < 4; t++) {
    const tablePile = tableau[t];
    if (tablePile.length > 0) {
      const topCard = tablePile[tablePile.length - 1];

      for (let f = 0; f < 4; f++) {
        if (canPlayOnFoundation(topCard, f as FoundationIndex, foundations)) {
          return false; // Move is possible
        }
      }
    }
  }

  return true; // No moves possible
};
